import { useId, useState, type ReactNode } from 'react';
import { TRACKER_NAMES, type TrackerId } from '../api/types';
import { median, usd } from '../lib/format';

/* The chart kit. Every chart here obeys the same few rules:
   - colour follows the thing (a tracker, a casino), never its rank;
   - marks are solid, 2px lines, rounded data-ends, square at the baseline;
   - a value is always readable without hovering: a readout, a label or a table;
   - "not published" is drawn as a gap, never as zero. */

export const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
export const shortDay = (d: string) =>
  new Date(`${d.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

type Fmt = (v: number | null | undefined) => string;

/* ------------------------------------------------------------------ rail */

export interface Reading { id: TrackerId; value: number; counted?: boolean }

/** Every tracker's reading for one quantity, on one axis, median marked. */
export function Rail({ readings, compact = false }: { readings: Reading[]; compact?: boolean }) {
  const sorted = [...readings].sort((a, b) => a.value - b.value);
  if (sorted.length < 2) return <p className="empty">One tracker only. Nothing to compare.</p>;
  const counted = sorted.filter((r) => r.counted !== false);
  const scale = counted.length >= 2 ? counted : sorted;
  const values = scale.map((r) => r.value);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || Math.abs(hi) || 1;
  const pad = span * 0.2;
  const at = (v: number) => Math.max(0, Math.min(100, ((v - (lo - pad)) / (span + pad * 2)) * 100));
  const mid = median(values);

  return (
    <div className={`rail${compact ? ' compact' : ''}`}>
      <div className="rail-plot" role="img" aria-label={sorted.map((r) => `${TRACKER_NAMES[r.id]} ${usd(r.value)}`).join(', ')}>
        <span className="rail-axis" />
        <span className="rail-span" style={{ left: `${at(lo)}%`, width: `${at(hi) - at(lo)}%` }} />
        {mid !== null && <span className="rail-mid" style={{ left: `${at(mid)}%` }} />}
        {sorted.map((r) => (
          <span
            key={r.id}
            className={`rail-dot${r.counted === false ? ' is-ref' : ''}`}
            style={{ left: `${at(r.value)}%`, color: `var(--tracker-${r.id})` }}
            title={`${TRACKER_NAMES[r.id]} ${usd(r.value)}`}
          />
        ))}
      </div>
      {!compact && (
        <div className="rail-key">
          {sorted.map((r) => (
            <span key={r.id}>
              <i className={`swatch${r.counted === false ? ' hollow' : ''}`} style={{ background: `var(--tracker-${r.id})`, color: `var(--tracker-${r.id})` }} aria-hidden />
              {TRACKER_NAMES[r.id]}
              {r.counted === false && <em>reference</em>}
              <b>{usd(r.value)}</b>
            </span>
          ))}
          {mid !== null && <span className="rail-median"><i className="line-key" aria-hidden />Median<b>{usd(mid)}</b></span>}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- donut */

export function Donut({ share, label }: { share: number; label: string }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  return (
    <div className="donut-wrap">
      <svg viewBox="0 0 100 100" role="img" aria-label={`${Math.round(share)}% ${label}`}>
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--card-2)" strokeWidth="12" />
        <circle
          className="donut-arc" cx="50" cy="50" r={r} fill="none" stroke="var(--blue)" strokeWidth="12"
          strokeLinecap="round" strokeDasharray={`${(share / 100) * c} ${c}`}
        />
      </svg>
      <div className="donut-center"><b>{Math.round(share)}%</b><span>{label}</span></div>
    </div>
  );
}

/* ------------------------------------------------------------ daily bars */

/** One reading per day, in duelapi's bar style, with a readout that follows the pointer. */
export function DailyBars({ points, color = 'var(--blue)', format = usd, height = 150 }: {
  points: { day: string; value: number | null }[]; color?: string; format?: Fmt; height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const vals = points.map((p) => p.value).filter(finite);
  const first = points[0];
  const last = points[points.length - 1];
  if (vals.length < 2 || !first || !last) return <p className="empty">Not enough published days.</p>;
  const top = Math.max(...vals);
  const mid = median(vals);
  const active = (hover !== null ? points[hover] : undefined) ?? last;

  return (
    <div className="daily">
      <div className="readout">
        <b>{format(active.value)}</b>
        <span>{shortDay(active.day)}{hover === null ? ' · latest' : ''}</span>
      </div>
      <div className="bars daily-bars" style={{ height }} onMouseLeave={() => setHover(null)}>
        {points.map((p, i) => (
          <span
            key={p.day} tabIndex={0}
            className={`bar${finite(p.value) ? '' : ' miss'}${hover === i ? ' on' : ''}`}
            onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
            aria-label={`${shortDay(p.day)}: ${format(p.value)}`}
          >
            <i style={{ height: `${finite(p.value) ? Math.max(2, (p.value / top) * 100) : 0}%`, background: color, animationDelay: `${i * 18}ms` }} />
          </span>
        ))}
        {mid !== null && <span className="threshold" style={{ bottom: `${(mid / top) * 100}%` }} aria-hidden />}
      </div>
      <div className="axis-row"><span>{shortDay(first.day)}</span><span>median {format(mid)}</span><span>{shortDay(last.day)}</span></div>
    </div>
  );
}

/* ----------------------------------------------------------------- lines */

export interface LineSeries { key: string; name: string; color: string; points: { day: string; value: number }[] }

/** Several series on one time axis: gradient wash, glow, crosshair and a readout of every series. */
export function Lines({ series, format = usd, height = 230 }: { series: LineSeries[]; format?: Fmt; height?: number }) {
  const uid = useId().replace(/:/g, '');
  const [hover, setHover] = useState<string | null>(null);
  const live = series.filter((s) => s.points.length >= 2);
  const days = [...new Set(live.flatMap((s) => s.points.map((p) => p.day)))].sort();
  if (live.length === 0 || days.length < 2) return <p className="empty">No tracker publishes a daily series for this.</p>;

  const W = 640, H = height, L = 58, R = 14, T = 14, B = 28;
  const vals = live.flatMap((s) => s.points.map((p) => p.value));
  const hi = Math.max(...vals, 1) * 1.08;
  const t0 = Date.parse(days[0] as string);
  const t1 = Date.parse(days[days.length - 1] as string);
  const x = (d: string) => L + ((Date.parse(d) - t0) / (t1 - t0 || 1)) * (W - L - R);
  const y = (v: number) => T + (1 - v / hi) * (H - T - B);
  const active = hover ?? (days[days.length - 1] as string);

  const move = (e: React.PointerEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * W;
    let best = days[0] as string;
    for (const d of days) if (Math.abs(x(d) - px) < Math.abs(x(best) - px)) best = d;
    setHover(best);
  };

  return (
    <div className="lines">
      <div className="legend">
        {live.map((s) => {
          const p = s.points.find((q) => q.day === active);
          return (
            <span key={s.key}>
              <i className="line-swatch" style={{ background: s.color }} aria-hidden />
              {s.name}
              <b>{p ? format(p.value) : '—'}</b>
            </span>
          );
        })}
        <span className="legend-day">{shortDay(active)}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="lines-svg" onPointerMove={move} onPointerLeave={() => setHover(null)} role="img"
        aria-label={`${live.map((s) => s.name).join(', ')}, ${shortDay(days[0] as string)} to ${shortDay(days[days.length - 1] as string)}`}>
        <defs>
          <clipPath id={`${uid}-sweep`}><rect className="sweep" x={L} y="0" width={W - L - R} height={H} /></clipPath>
          {live.map((s) => (
            <linearGradient key={s.key} id={`${uid}-${s.key}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" style={{ stopColor: s.color, stopOpacity: live.length === 1 ? 0.32 : 0.18 }} />
              <stop offset="100%" style={{ stopColor: s.color, stopOpacity: 0 }} />
            </linearGradient>
          ))}
        </defs>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={L} x2={W - R} y1={y(hi * f / 1.08)} y2={y(hi * f / 1.08)} className="grid" />
            <text x={L - 8} y={y(hi * f / 1.08) + 4} textAnchor="end">{format(hi * f / 1.08)}</text>
          </g>
        ))}
        <g clipPath={`url(#${uid}-sweep)`}>
          {live.map((s) => {
            const pts = [...s.points].sort((a, b) => a.day.localeCompare(b.day));
            const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.day).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
            const f = pts[0] as { day: string };
            const l = pts[pts.length - 1] as { day: string };
            return (
              <g key={s.key} style={{ filter: `drop-shadow(0 4px 8px color-mix(in srgb, ${s.color} 35%, transparent))` }}>
                <path d={`${line} L${x(l.day)},${y(0)} L${x(f.day)},${y(0)} Z`} fill={`url(#${uid}-${s.key})`} />
                <path d={line} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              </g>
            );
          })}
        </g>
        <line x1={x(active)} x2={x(active)} y1={T} y2={H - B} className="crosshair" />
        {live.map((s) => {
          const p = s.points.find((q) => q.day === active);
          return p ? <circle key={s.key} cx={x(p.day)} cy={y(p.value)} r="5" className="dot" style={{ fill: s.color }} /> : null;
        })}
        <text x={L} y={H - 6}>{shortDay(days[0] as string)}</text>
        <text x={W - R} y={H - 6} textAnchor="end">{shortDay(days[days.length - 1] as string)}</text>
      </svg>
    </div>
  );
}

/* ------------------------------------------------------ horizontal bars */

export interface HBar {
  key: string; label: ReactNode; value: number; display: string;
  color?: string; className?: string; href?: string;
  dots?: { key: string; color: string; value: number; title: string; hollow?: boolean }[];
}

/** A ranked list where length is the point: gaps, casinos, chains. */
export function HBars({ items, max }: { items: HBar[]; max?: number }) {
  if (!items.length) return <p className="empty">Nothing published here.</p>;
  const top = max ?? Math.max(...items.map((i) => i.value), ...items.flatMap((i) => (i.dots ?? []).map((d) => d.value)), 1);
  return (
    <div className="hbars">
      {items.map((it, i) => {
        const body = (
          <>
            <span className="hbar-label">{it.label}</span>
            <span className="hbar-track">
              <i className={it.className} style={{ width: `${Math.max(1.5, (it.value / top) * 100)}%`, animationDelay: `${i * 50}ms`, ...(it.color ? { ['--gb-color' as string]: it.color } : {}) }} />
              {(it.dots ?? []).map((d) => (
                <span key={d.key} className={`hbar-dot${d.hollow ? ' hollow' : ''}`} title={d.title}
                  style={{ left: `${Math.min(100, (d.value / top) * 100)}%`, color: d.color }} />
              ))}
            </span>
            <b className="hbar-value">{it.display}</b>
          </>
        );
        return it.href
          ? <a key={it.key} href={it.href} className="hbar">{body}</a>
          : <div key={it.key} className="hbar">{body}</div>;
      })}
    </div>
  );
}

/* --------------------------------------------------------- grouped bars */

/** One group per bucket, one bar per tracker. For deposit-size distributions. */
export function Grouped({ groups, series, format }: {
  groups: string[]; series: { key: string; name: string; color: string; values: (number | null)[] }[]; format: Fmt;
}) {
  const all = series.flatMap((s) => s.values).filter(finite);
  if (!groups.length || !all.length) return <p className="empty">No distribution published.</p>;
  const top = Math.max(...all, 1);
  return (
    <div className="grouped">
      <div className="legend">
        {series.map((s) => <span key={s.key}><i className="swatch" style={{ background: s.color }} aria-hidden />{s.name}</span>)}
      </div>
      <div className="grouped-plot">
        {groups.map((g, gi) => (
          <div key={g} className="grouped-col">
            <div className="grouped-bars">
              {series.map((s) => {
                const v = s.values[gi];
                return (
                  <span key={s.key} className="gbar" title={`${s.name}, ${g}: ${format(v)}`}>
                    <i style={{ height: `${finite(v) ? Math.max(2, (v / top) * 100) : 0}%`, background: s.color }} />
                  </span>
                );
              })}
            </div>
            <span className="grouped-label">{g}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
